import { useCallback, useContext, useEffect, useRef, useState } from "react";

import logo from "../../Assets/logo.png";
import Context from "../../Context";
import Collapsible from "./Collapsible";
import { createIFrame, loadTabaPaySDK } from "./createIFrame";
import styles from "./index.module.scss";

const DebitTPIFrame = () => {
  const context = useContext(Context);
  const queryParams = new URLSearchParams(window.location.search);
  const callback_uri = queryParams.get("callback_uri");
  const urlToken = queryParams.get("token");
  // Use relative URL in development to work with proxy, full URL in production
  const apiUrl =
    process.env.NODE_ENV === "development"
      ? ""
      : process.env.REACT_APP_API_HOST;

  // Usar el token del Context, y si no existe, usar el de la URL
  const authToken = context.token || urlToken;

  // Estados para los inputs
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [openPersonal, setOpenPersonal] = useState(true);
  const [openCard, setOpenCard] = useState(false);
  const isPersonalValid = firstName.trim() !== "" && lastName.trim() !== "";

  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const callbackUrl = useCallback(
    (status: string) => {
      if (callback_uri) {
        let decodedURI = atob(callback_uri);
        let uri = decodedURI + "?status=" + status;
        window.location.replace(uri);
        return;
      }

      window.parent.postMessage(
        {
          appname: "XPAY-LADONWARE-API",
          status: status,
        },
        "*",
      );
    },
    [callback_uri],
  );

  const createAccount = useCallback(
    async (params: any) => {
      setIsLoading(true);
      try {
        const body = {
          token: params.token,
          firstname: params.firstName,
          lastname: params.lastName,
          expDate: params.expDate,
        };

        const response = await fetch(apiUrl + "/api/addCard", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + authToken,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Server error response:", errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        callbackUrl("SUCCESS");
      } catch (error) {
        console.error("Error creating account:", error);
        alert("Error processing card. Please try again.");
        callbackUrl("ERROR");
      } finally {
        setIsLoading(false);
      }
    },
    [authToken, callbackUrl, firstName, lastName],
  );

  useEffect(() => {
    const loadSDK = async () => {
      try {
        await loadTabaPaySDK();
        setSdkLoaded(true);
      } catch (error) {
        console.error("Error loading TabaPay SDK:", error);
        alert("Error loading payment form. Please reload the page.");
      }
    };
    loadSDK();
  }, []);

  useEffect(() => {
    console.log("=== useEffect iframe creation triggered ===");
    console.log("States:", {
      openCard,
      sdkLoaded,
      isLoading,
      firstName,
      lastName,
      isPersonalValid,
    });
    //console.log('containerRef.current:', containerRef.current);
    //console.log('containerRef.current type:', typeof containerRef.current);
    if (containerRef.current) {
      console.log("Container element:", {
        tagName: containerRef.current.tagName,
        className: containerRef.current.className,
        id: containerRef.current.id,
        offsetWidth: containerRef.current.offsetWidth,
        offsetHeight: containerRef.current.offsetHeight,
        style: containerRef.current.style.cssText,
        parentElement: containerRef.current.parentElement?.tagName,
      });
    }

    // Solo intentar crear el iframe si el colapsable está abierto
    if (!openCard) {
      console.log("❌ Card Details section is closed, not creating iframe");
      return;
    }

    if (!sdkLoaded) {
      console.log("❌ SDK not loaded yet");
      return;
    }

    if (!containerRef.current) {
      // console.log('❌ Container not ready yet - containerRef.current is:', containerRef.current);
      // Intentar nuevamente en un breve momento
      const timer = setTimeout(() => {
        // console.log('⏰ Timer triggered - checking again...');
        // console.log('containerRef.current after timeout:', containerRef.current);
        // console.log('openCard after timeout:', openCard);
        if (containerRef.current && openCard) {
          // console.log('✅ Container is now ready, retrying iframe creation');
          // Forzar re-renderizado para intentar crear el iframe nuevamente
          setSdkLoaded((prev: boolean) => {
            if (prev) return prev; // No cambiar si ya está true
            return true; // Solo para forzar el re-renderizado
          });
        } else {
          // console.log('❌ Still not ready after timeout');
        }
      }, 300);
      return () => {
        // console.log('🧹 Cleaning up timer');
        clearTimeout(timer);
      };
    }

    // console.log('✅ All conditions met - Creating iframe: SDK loaded and container ready');

    const handleFocusChange = (focused: boolean) => {};

    const handleSubmit = (data: any) => {
      if (!isLoading) {
        const fullToken = typeof data === "string" ? data : data?.token;

        if (!fullToken) {
          console.error("Missing token");
          alert("Error processing card. Please try again.");
          return;
        }

        const tokenParts = fullToken.split("|");
        if (tokenParts.length >= 3) {
          const expDate = tokenParts[1];
          const token = tokenParts[2];

          createAccount({
            token,
            expDate,
            firstName,
            lastName,
          });
        } else {
          console.error("Invalid token format");
          alert("Error processing card. Please try again.");
        }
      }
    };

    const handleCancel = () => {
      // console.log('Cancel handler called');
      callbackUrl("CANCELLED");
    };

    try {
      const iframe = createIFrame(
        handleFocusChange,
        handleSubmit,
        handleCancel,
      );

      if (iframe?.element) {
        const container = containerRef.current;
        while (container?.firstChild) {
          container.removeChild(container.firstChild);
        }
        iframe.element.style.width = "100%";
        iframe.element.style.maxWidth = "500px";
        iframe.element.style.margin = "0 auto";
        iframe.element.style.display = "block";
        iframe.element.style.backgroundColor = "white";
        container?.appendChild(iframe.element);
      }
    } catch (error) {
      console.error("Error creating TabaPay iframe:", error);
      alert("Error initializing payment form. Please reload the page.");
    }
  }, [sdkLoaded, createAccount, callbackUrl, isLoading, openCard]);

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#F8F9FA",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "12px",
      }}
    >
      <div className={styles.logoImg} style={{ marginBottom: "20px" }}>
        <img src={logo} alt="Logo" />
      </div>
      <div style={{ width: "100%", maxWidth: "440px", padding: "0 4px" }}>
        <Collapsible
          title="Name on Card"
          open={openPersonal}
          onToggle={() => {
            setOpenPersonal((open) => !open);
            if (!openPersonal && isPersonalValid) setOpenCard(true);
          }}
          disabled={false}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              width: "100%",
            }}
          >
            <div className={styles.textfield}>
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                placeholder="Jesus"
                value={firstName}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^a-zA-Z\s]/g, "")
                    .slice(0, 30);
                  setFirstName(value);
                  if (value.trim() === "" || lastName.trim() === "") {
                    setOpenCard(false);
                    setOpenPersonal(true);
                  }
                }}
                required
              />
            </div>
            <div className={styles.textfield}>
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                placeholder="Francisco"
                value={lastName}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^a-zA-Z\s]/g, "")
                    .slice(0, 30);
                  setLastName(value);
                  if (firstName.trim() === "" || value.trim() === "") {
                    setOpenCard(false);
                    setOpenPersonal(true);
                  }
                }}
                required
              />
            </div>
          </div>
        </Collapsible>
        <Collapsible
          title="Card Details"
          open={openCard}
          onToggle={() => {
            if (isPersonalValid) setOpenCard((open) => !open);
          }}
          disabled={!isPersonalValid}
        >
          <div
            ref={containerRef}
            style={{
              width: "100%",
              marginLeft: "-10px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              minHeight: "400px",
              maxHeight: "500px",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          />
        </Collapsible>
      </div>

      {isLoading && (
        <div className={styles.overlay}>
          <div className={styles.spinner}>Loading...</div>
        </div>
      )}
    </div>
  );
};

export default DebitTPIFrame;
